import { useState } from "react";
import { X, ChevronLeft, ChevronRight, Image as ImageIcon, Sparkles, ChevronDown, ChevronUp } from "lucide-react";
import { siteConfig } from "@/config/siteConfig";
import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";

export function BusGallerySection() {
  const [selectedImage, setSelectedImage] = useState<number | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [isGalleryExpanded, setIsGalleryExpanded] = useState(false);

  // Don't render if disabled
  if (!siteConfig.additionalBusImages.enabled) {
    return null;
  }

  const images = siteConfig.additionalBusImages.images;
  
  // Filter images by category
  const filteredImages = selectedCategory === "all" 
    ? images 
    : images.filter(img => img.category === selectedCategory);

  // Get unique categories
  const categories = ["all", ...Array.from(new Set(images.map(img => img.category)))];

  // Open lightbox
  const openLightbox = (imageId: number) => {
    setSelectedImage(imageId);
    document.body.style.overflow = "hidden";
  };

  // Close lightbox
  const closeLightbox = () => {
    setSelectedImage(null);
    document.body.style.overflow = "unset";
  };

  // Navigate lightbox
  const navigateLightbox = (direction: "prev" | "next") => {
    if (selectedImage === null) return;
    
    const currentIndex = filteredImages.findIndex(img => img.id === selectedImage);
    let newIndex;
    
    if (direction === "prev") {
      newIndex = currentIndex > 0 ? currentIndex - 1 : filteredImages.length - 1;
    } else {
      newIndex = currentIndex < filteredImages.length - 1 ? currentIndex + 1 : 0;
    }
    
    setSelectedImage(filteredImages[newIndex].id);
  };

  // Get current image for lightbox
  const currentImage = selectedImage ? filteredImages.find(img => img.id === selectedImage) : null;

  return (
    <section id="bus-gallery" className="section-padding bg-secondary/20 relative overflow-hidden">
      {/* Enhanced Background elements with floating particles */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 right-0 w-96 h-96 bg-primary/5 rounded-full blur-3xl animate-float" />
        <div className="absolute bottom-0 left-1/4 w-64 h-64 bg-primary/5 rounded-full blur-3xl animate-float animation-delay-200" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-32 h-32 bg-primary/3 rounded-full blur-2xl animate-pulse-glow" />
        
        {/* Floating particles */}
        <div className="absolute top-1/6 left-1/6 w-2 h-2 bg-primary/40 rounded-full animate-float animation-delay-300" />
        <div className="absolute top-1/3 right-1/4 w-1.5 h-1.5 bg-primary/50 rounded-full animate-float animation-delay-500" />
        <div className="absolute bottom-1/3 left-1/3 w-1 h-1 bg-primary/60 rounded-full animate-float animation-delay-700" />
        <div className="absolute bottom-1/4 right-1/6 w-2.5 h-2.5 bg-primary/30 rounded-full animate-float animation-delay-400" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Enhanced Section Header */}
        <div className="text-center max-w-2xl mx-auto mb-16 animate-fade-in-up">
          <div className="inline-block px-4 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6">
            <Sparkles className="w-4 h-4 inline-block mr-2" />
            Our Fleet
          </div>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
            Bus <span className="text-gradient">Gallery</span>
          </h2>
          <p className="text-muted-foreground text-lg">
            Explore our modern fleet and comfortable interiors that make your journey memorable.
          </p>
        </div>

        {/* Desktop: Always visible gallery */}
        <div className="hidden md:block">
          {/* Enhanced Category Filter */}
          <div className="flex flex-wrap justify-center gap-3 mb-12 animate-fade-in-up">
            {categories.map((category) => (
              <Button
                key={category}
                variant={selectedCategory === category ? "default" : "outline"}
                onClick={() => setSelectedCategory(category)}
                className={`capitalize transition-all duration-300 ${
                  selectedCategory === category
                    ? "bg-primary hover:bg-primary/90 text-primary-foreground"
                    : "border-primary/50 hover:bg-primary/10"
                }`}
              >
                {category === "all" ? "All Images" : category}
              </Button>
            ))}
          </div>

          {/* Enhanced Images Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8 max-w-7xl mx-auto animate-fade-in-up">
            {filteredImages.map((image, index) => (
              <div
                key={image.id}
                className="group cursor-pointer relative overflow-hidden rounded-2xl border border-border/50 transition-all duration-500 hover:border-primary/30 transform-gpu"
                style={{ animationDelay: `${index * 100}ms` }}
                onClick={() => openLightbox(image.id)}
              >
                <div className="aspect-[4/3] overflow-hidden relative">
                  <img
                    src={image.path}
                    alt={image.alt}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    loading="lazy"
                  />
                  
                  {/* Subtle overlay on hover */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-500">
                    <div className="absolute bottom-4 left-4 right-4 transform translate-y-2 group-hover:translate-y-0 transition-transform duration-300">
                      <h3 className="text-white font-medium text-sm mb-2 drop-shadow-lg">{image.alt}</h3>
                      <div className="flex items-center gap-2">
                        <ImageIcon className="w-4 h-4 text-white/90" />
                        <span className="text-white/90 text-xs capitalize">{image.category}</span>
                      </div>
                    </div>
                  </div>

                  {/* Enhanced featured badge */}
                  {image.featured && (
                    <div className="absolute top-3 right-3">
                      <div className="bg-primary/90 backdrop-blur-sm text-primary-foreground text-xs px-3 py-1 rounded-full font-medium">
                        Featured
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Mobile: Collapsible gallery */}
        <div className="md:hidden">
          <Collapsible open={isGalleryExpanded} onOpenChange={setIsGalleryExpanded}>
            <CollapsibleTrigger asChild>
              <Button
                variant="outline"
                className="w-full mb-6 flex items-center justify-between border-primary/50 hover:bg-primary/10"
              >
                <span className="font-medium">
                  {isGalleryExpanded ? "Hide Gallery" : "Show Gallery"}
                </span>
                {isGalleryExpanded ? (
                  <ChevronUp className="w-4 h-4" />
                ) : (
                  <ChevronDown className="w-4 h-4" />
                )}
              </Button>
            </CollapsibleTrigger>
            
            <CollapsibleContent>
              {/* Mobile Category Filter */}
              <div className="flex flex-wrap justify-center gap-3 mb-8 animate-fade-in-up">
                {categories.map((category) => (
                  <Button
                    key={category}
                    variant={selectedCategory === category ? "default" : "outline"}
                    onClick={() => setSelectedCategory(category)}
                    size="sm"
                    className={`capitalize transition-all duration-300 ${
                      selectedCategory === category
                        ? "bg-primary hover:bg-primary/90 text-primary-foreground"
                        : "border-primary/50 hover:bg-primary/10"
                    }`}
                  >
                    {category === "all" ? "All Images" : category}
                  </Button>
                ))}
              </div>

              {/* Mobile Images Grid */}
              <div className="grid grid-cols-2 gap-4 animate-fade-in-up">
                {filteredImages.map((image, index) => (
                  <div
                    key={image.id}
                    className="group cursor-pointer relative overflow-hidden rounded-xl border border-border/50 transition-all duration-500 hover:border-primary/30"
                    style={{ animationDelay: `${index * 50}ms` }}
                    onClick={() => openLightbox(image.id)}
                  >
                    <div className="aspect-[4/3] overflow-hidden relative">
                      <img
                        src={image.path}
                        alt={image.alt}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                        loading="lazy"
                      />
                      
                      {/* Mobile overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-500">
                        <div className="absolute bottom-2 left-2 right-2">
                          <h3 className="text-white font-medium text-xs mb-1 drop-shadow-lg">{image.alt}</h3>
                          <div className="flex items-center gap-1">
                            <ImageIcon className="w-3 h-3 text-white/90" />
                            <span className="text-white/90 text-xs capitalize">{image.category}</span>
                          </div>
                        </div>
                      </div>

                      {/* Mobile featured badge */}
                      {image.featured && (
                        <div className="absolute top-2 right-2">
                          <div className="bg-primary/90 backdrop-blur-sm text-primary-foreground text-xs px-2 py-0.5 rounded-full font-medium">
                            Featured
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CollapsibleContent>
          </Collapsible>
        </div>

        {/* Enhanced Empty state */}
        {filteredImages.length === 0 && (
          <div className="text-center py-16">
            <div className="p-8 rounded-2xl border border-border/50 max-w-md mx-auto">
              <ImageIcon className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium text-foreground mb-2">No images found</h3>
              <p className="text-muted-foreground">
                No images found for the selected category.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Enhanced Lightbox */}
      {selectedImage && currentImage && (
        <div className="fixed inset-0 z-50 bg-black/95 backdrop-blur-lg flex items-center justify-center p-4">
          {/* Enhanced close button */}
          <button
            onClick={closeLightbox}
            title="Close gallery"
            className="absolute top-6 right-6 z-10 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-sm flex items-center justify-center text-white transition-all duration-300"
          >
            <X className="w-6 h-6" />
          </button>

          {/* Enhanced navigation buttons */}
          {filteredImages.length > 1 && (
            <>
              <button
                onClick={() => navigateLightbox("prev")}
                title="Previous image"
                className="absolute left-6 top-1/2 -translate-y-1/2 z-10 w-14 h-14 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-sm flex items-center justify-center text-white transition-all duration-300"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              <button
                onClick={() => navigateLightbox("next")}
                title="Next image"
                className="absolute right-6 top-1/2 -translate-y-1/2 z-10 w-14 h-14 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-sm flex items-center justify-center text-white transition-all duration-300"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
            </>
          )}

          {/* Enhanced Image */}
          <div className="max-w-5xl max-h-[90vh] relative">
            <img
              src={currentImage.path}
              alt={currentImage.alt}
              className="max-w-full max-h-full object-contain rounded-xl shadow-2xl"
            />
            
            {/* Enhanced image info */}
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 via-black/70 to-transparent p-6 rounded-b-xl backdrop-blur-sm">
              <h3 className="text-white text-lg font-medium mb-2 drop-shadow-lg">{currentImage.alt}</h3>
              <p className="text-white/90 text-sm capitalize">{currentImage.category}</p>
            </div>
          </div>

          {/* Click outside to close */}
          <div 
            className="absolute inset-0 -z-10" 
            onClick={closeLightbox}
          />
        </div>
      )}
    </section>
  );
}
